/**
 * Broker-account hooks — Settings → Brokers data (full CRUD).
 * Backed by /api/v1/broker-accounts (compass.api.broker_accounts_api).
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiDelete, apiGet, apiPost, apiPut } from '@/lib/api/client'
import { queryKeys } from '@/lib/queryKeys'

export interface BrokerAccount {
  id: string
  alias: string
  ib_account_id: string
  account_name: string
  broker_type: string
  is_active: boolean
  /** Market data routes through the default account's gateway. */
  is_default: boolean
  /**
   * When true, skip this account in portfolio-metric rollups (Dashboard totals,
   * aggregated risk/positions, income scope=all, sidebar All Accounts). Still
   * selectable for single-account views and trading. Paper defaults true.
   */
  exclude_from_metrics: boolean
  /** Per-account: include in the scheduled Flex import. */
  flex_import_enabled: boolean
  /** CFM basis guard: block below-basis covered-call writes/rolls on this account. */
  basis_enforcement_enabled: boolean
  flex_query_id: string | null
  has_flex_token: boolean
  display_order: number
  timezone: string
  tenant_id: string | null
  tenant_name: string | null
  gateway_id: string | null
  gateway_name: string | null
  created_at: string | null
  updated_at: string | null
}

export interface BrokerAccountUpsert {
  alias?: string
  ib_account_id?: string
  account_name?: string
  tenant_id?: string
  /** explicit null clears the connection binding */
  gateway_id?: string | null
  timezone?: string
  is_active?: boolean
  is_default?: boolean
  exclude_from_metrics?: boolean
  flex_import_enabled?: boolean
  basis_enforcement_enabled?: boolean
  display_order?: number
}

const QUERY_KEY = queryKeys.brokerAccounts.all

export function useBrokerAccounts() {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => apiGet<{ accounts: BrokerAccount[] }>('/broker-accounts'),
    refetchInterval: 30_000,
  })
}

export function useCreateBrokerAccount() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: BrokerAccountUpsert) =>
      apiPost<BrokerAccount>('/broker-accounts', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  })
}

export function useUpdateBrokerAccount() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...body }: BrokerAccountUpsert & { id: string }) =>
      apiPut<BrokerAccount>(`/broker-accounts/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  })
}

export function useDeleteBrokerAccount() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/broker-accounts/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  })
}

export interface TenantOption {
  id: string
  slug: string
  name: string
}

export function useTenantOptions() {
  return useQuery({
    queryKey: queryKeys.tenants.options(),
    queryFn: () => apiGet<{ tenants: TenantOption[] }>('/tenants'),
    staleTime: 60_000,
  })
}
